import React, { memo, useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * ChainFlow OS · PremiumSegmented
 * ────────────────────────────────────────────────────────────
 * The third foundational interaction primitive — for low-cardinality
 * **categorical** value selection. Where `PremiumStepper` handles
 * discrete numeric precision and `PremiumRange` handles continuous
 * sweep, `PremiumSegmented` handles a small, named set of operational
 * choices: weekly / monthly / quarterly horizons, risk severity tiers,
 * Mission Control mode switches, office comparison toggles,
 * communication visibility modes, timeline grouping modes, recovery
 * priority toggles, confidence-state filters, and any other
 * operational mode switch shipped on the platform.
 *
 * Locked v1 interaction grammar:
 *  • Pointer Events with `touch-action: manipulation` for mouse + touch
 *    parity. No double-tap zoom, no native context menu.
 *  • Keyboard discipline (focused segment):
 *       ←  ↑  : select previous option (wraps).
 *       →  ↓  : select next option (wraps).
 *       Home  : first option.
 *       End   : last option.
 *       Space / Enter : activate focused segment (also fired by click).
 *  • ARIA grammar: role="radiogroup" on the container with role="radio"
 *    on each segment; `aria-checked`, `tabIndex` follow the roving-
 *    focus pattern (only the active segment receives tab focus).
 *  • Disabled at control level (whole strip) AND per-segment.
 *
 * Locked v1 visual identity:
 *  • Soft slate-100 segmented surface with inset top highlight.
 *  • Active segment sits under a **moving white pill** that translates
 *    behind the selected option with a calm 280 ms cubic-bezier — the
 *    same motion family as the stepper crossfade and the range thumb.
 *  • Inactive labels in `text-slate-500`, active label in slate-900,
 *    hover lifts inactive labels to slate-700.
 *  • Violet focus-visible ring (matches the rest of the brand
 *    primitives).
 *  • `tabular-nums` baseline so numeric labels never shimmer.
 *  • `prefers-reduced-motion` collapses the pill slide to an instant
 *    swap.
 *
 * @prop {string|number} value          Active segment value.
 * @prop {(v) => void} onChange         Called with the next value.
 * @prop {Array<{value, label, disabled?}>} options
 *   Equal-width segments rendered in order.
 * @prop {"sm"|"md"|"lg"} [size="md"]
 * @prop {boolean} [disabled=false]     Whole control disabled.
 * @prop {boolean} [fullWidth=false]    Span container width vs auto.
 * @prop {boolean} [autoWidth=false]    v1.1 · Content-sized segments
 *   (default: equal-width). Use when label lengths vary or the option
 *   count is dynamic. Sliding pill measures the active segment's real
 *   offset + width at runtime; same locked motion grammar.
 * @prop {"light"|"dark"} [tone="light"]
 *   v1.1 · Adapts surface / pill / label colours for dark operational
 *   surfaces (slate-900 / navy panels). Identical interaction grammar
 *   and motion family — only tonal colour shifts.
 * @prop {string} [ariaLabel]           Required if no visible label.
 * @prop {string} [testid]              Yields `testid`, `testid-<value>`.
 * @prop {string} [className]
 */
export const PremiumSegmented = memo(function PremiumSegmented({
  value,
  onChange,
  options,
  size = "md",
  disabled = false,
  fullWidth = false,
  autoWidth = false,
  tone = "light",
  ariaLabel,
  testid,
  className = "",
}) {
  const groupId = useId();
  const buttonsRef = useRef([]);
  const containerRef = useRef(null);

  // autoWidth measurement — the active segment's real offset + width.
  // Equal-width mode keeps the original percentage-based positioning so
  // this state is unused (and the layout effect is skipped).
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const activeIndex = useMemo(() => {
    const i = options.findIndex((o) => o.value === value);
    return i >= 0 ? i : 0;
  }, [options, value]);

  const select = useCallback((idx) => {
    const opt = options[idx];
    if (!opt || opt.disabled || disabled) return;
    if (opt.value !== value) onChange(opt.value);
  }, [options, onChange, value, disabled]);

  const focusAt = useCallback((idx) => {
    const btn = buttonsRef.current[idx];
    if (btn) btn.focus();
  }, []);

  const stepFocus = useCallback((from, dir) => {
    const n = options.length;
    if (n === 0) return;
    let i = from;
    // Skip disabled options (and don't loop forever).
    for (let k = 0; k < n; k++) {
      i = (i + dir + n) % n;
      if (!options[i]?.disabled) break;
    }
    focusAt(i);
    select(i);
  }, [options, focusAt, select]);

  const onKeyDown = useCallback((e, idx) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":   e.preventDefault(); stepFocus(idx, -1); break;
      case "ArrowRight":
      case "ArrowDown": e.preventDefault(); stepFocus(idx, +1); break;
      case "Home":      e.preventDefault(); focusAt(0); select(0); break;
      case "End":       e.preventDefault(); {
        const last = options.length - 1;
        focusAt(last); select(last); break;
      }
      case " ":
      case "Enter":     e.preventDefault(); select(idx); break;
      default: return;
    }
  }, [disabled, options.length, stepFocus, focusAt, select]);

  const sizing = {
    sm: { wrap: "p-0.5 rounded-lg",  btn: "h-7 px-2.5",   text: "text-[11.5px]" },
    md: { wrap: "p-1 rounded-xl",    btn: "h-8 px-3.5",   text: "text-[12.5px]" },
    lg: { wrap: "p-1 rounded-xl",    btn: "h-9 px-4",     text: "text-[13.5px]" },
  }[size] || { wrap: "p-1 rounded-xl", btn: "h-8 px-3.5", text: "text-[12.5px]" };

  const widthPct = options.length > 0 ? 100 / options.length : 100;
  const indicatorLeftPct = activeIndex * widthPct;

  // autoWidth: read the active segment's real offset + width whenever
  // it changes. Re-measure on window resize so responsive layouts stay
  // in sync. In equal-width (default) mode this layout effect is a
  // no-op cost: cheap reads, no DOM mutation.
  useLayoutEffect(() => {
    if (!autoWidth) return;
    const btn = buttonsRef.current[activeIndex];
    const container = containerRef.current;
    if (!btn || !container) return;
    const containerStyle = window.getComputedStyle(container);
    const padLeft = parseFloat(containerStyle.paddingLeft) || 0;
    // offsetLeft is relative to the offsetParent (container). Subtract
    // the container's padding so the indicator sits inside the inset
    // padding well, matching the equal-width geometry.
    const left = btn.offsetLeft;
    const width = btn.offsetWidth;
    setIndicator({ left, width, ready: true });
    // Track resize so the pill follows label reflows / responsive width.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const b = buttonsRef.current[activeIndex];
      if (!b) return;
      setIndicator({ left: b.offsetLeft, width: b.offsetWidth, ready: true });
    });
    ro.observe(container);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoWidth, activeIndex, options.length]);

  // Indicator positioning — branches between modes but keeps the same
  // motion grammar (locked v1 cubic-bezier transition lives in CSS).
  const indicatorStyle = autoWidth
    ? {
        left: indicator.ready ? `${indicator.left}px` : `calc(${indicatorLeftPct}% + 4px)`,
        width: indicator.ready ? `${indicator.width}px` : `calc(${widthPct}% - 8px)`,
        opacity: indicator.ready ? 1 : 0,
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.10), 0 4px 12px -4px rgba(79,70,229,0.16), inset 0 0 0 1px rgba(15,23,42,0.04)",
      }
    : {
        left: `calc(${indicatorLeftPct}% + 4px)`,
        width: `calc(${widthPct}% - 8px)`,
        boxShadow:
          "0 1px 2px rgba(15,23,42,0.10), 0 4px 12px -4px rgba(79,70,229,0.16), inset 0 0 0 1px rgba(15,23,42,0.04)",
      };

  const isDark = tone === "dark";
  // Match the dark cinematic operational surface used on PeoplePage /
  // ViewingsPage — slate-700 surface, slate-700/40 sliding pill,
  // slate-300 inactive labels, white active labels.
  const segSurfaceCls = isDark
    ? "bg-slate-800/60 border border-slate-700/60"
    : "bg-slate-100";
  const segInsetShadow = isDark
    ? "inset 0 1px 0 rgba(255,255,255,0.04)"
    : "inset 0 1px 0 rgba(255,255,255,0.6)";
  const pillBg = isDark ? "bg-slate-700/80" : "bg-white";
  const pillShadow = isDark
    ? "0 1px 2px rgba(0,0,0,0.25), 0 4px 12px -4px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.05)"
    : "0 1px 2px rgba(15,23,42,0.10), 0 4px 12px -4px rgba(79,70,229,0.16), inset 0 0 0 1px rgba(15,23,42,0.04)";

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={
        `cf-seg relative inline-flex ${segSurfaceCls} ${sizing.wrap} ` +
        `${fullWidth ? "w-full flex" : ""} ` +
        `${disabled ? "cf-seg--disabled" : ""} ${className}`
      }
      style={{ boxShadow: segInsetShadow }}
      data-testid={testid ? `${testid}-wrap` : undefined}
    >
      {/* Active indicator pill — slides behind the selected segment. */}
      <span
        aria-hidden="true"
        className={`cf-seg-indicator absolute top-1 bottom-1 rounded-[10px] ${pillBg}`}
        style={{ ...indicatorStyle, boxShadow: pillShadow }}
      />
      {options.map((opt, i) => {
        const active = i === activeIndex;
        const segDisabled = disabled || !!opt.disabled;
        return (
          <button
            key={opt.value}
            ref={(el) => { buttonsRef.current[i] = el; }}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={segDisabled || undefined}
            tabIndex={!disabled && active ? 0 : -1}
            disabled={segDisabled}
            onClick={() => select(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              width: autoWidth ? undefined : `${widthPct}%`,
              touchAction: "manipulation",
            }}
            data-testid={testid ? `${testid}-${opt.value}` : undefined}
            data-active={active || undefined}
            className={
              `cf-seg-btn relative z-[1] inline-flex items-center justify-center ` +
              `${sizing.btn} ${sizing.text} font-semibold tabular-nums select-none ` +
              `transition-colors duration-150 rounded-[10px] ` +
              `focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 ` +
              (active
                ? (isDark ? "text-white" : "text-slate-900")
                : (isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")) +
              (segDisabled ? " opacity-40 cursor-not-allowed" : " cursor-pointer")
            }
          >
            {opt.label}
          </button>
        );
      })}
      <style>{`
        .cf-seg-indicator {
          transition: left 280ms cubic-bezier(0.22, 1, 0.36, 1),
                      width 280ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: left, width;
        }
        .cf-seg-btn:active:not([disabled]) { transform: scale(0.97); }
        .cf-seg-btn { transition: color 150ms ease, transform 120ms ease; }
        .cf-seg--disabled { opacity: 0.6; }
        @media (prefers-reduced-motion: reduce) {
          .cf-seg-indicator { transition: none !important; }
          .cf-seg-btn { transition: none !important; }
          .cf-seg-btn:active:not([disabled]) { transform: none; }
        }
      `}</style>
    </div>
  );
});

export default PremiumSegmented;
